const { isUndefined, isArray, isString } = require('@hints/utils/types');
const config = require('./data/company-questions-config.json');
const { findByAddress, isEmpty, isset, asArray, distinctInArray } = require('@hints/utils/data');

const _scoreCheck = (path, checkCb) => (evaluation) => {
    const score = findByAddress(evaluation || {}, path);
    if(!isset(score)) return false;
    return checkCb(score);
};

const _scoreLT = (path, maxScore) => _scoreCheck(path, score => score < maxScore);
const _scoreGT = (path, minScore) => _scoreCheck(path, score => score > minScore);

const _mentalValidator = _scoreLT('evalMental.score', 66);
const _cerebralValidator = _scoreLT('evalCerebral.score', 66);

const CriteriaValidators = {
    sleep: _scoreLT('evalSleep.score', 50),
    emotional: _scoreLT('evalEmotional.score', 66),
    pain: _scoreGT('evalPhysical.painScore', 0), 
    activity: _scoreLT('evalPhysical.activityScore', 50),
    mental: _mentalValidator,
    cerebral: e => !_mentalValidator(e) && _cerebralValidator(e),
};

function getAllQuestionIds() { return config.questions.map(q => q.id); }

function isCriteriaAllowed(evaluation, criteria) {
    if(!CriteriaValidators[criteria]) return false;
    return CriteriaValidators[criteria](evaluation);
}

function getAllowedQuestionIds(evaluation, category) {
    const categoryConfig = config.categories.find(c => c.id === category);
    if(!isset(categoryConfig)) return [];
    const allowedCriterias = categoryConfig.criterias.filter(c => isCriteriaAllowed(evaluation, c.id));
    if(isEmpty(allowedCriterias)) return [];
    const allowedQuestionIds = distinctInArray(allowedCriterias.reduce((arr, c) => [...arr, ...c.questions], []));
    return allowedQuestionIds;
}

function hasAllowedQuestions(evaluation, category) {
    const allowedQuestionIds = getAllowedQuestionIds(evaluation, category);
    return !isEmpty(allowedQuestionIds);
}

function getAllowedQuestions(evaluation, category) {
    const allowedQuestionIds = getAllowedQuestionIds(evaluation, category);
    return allowedQuestionIds.map(qId => config.questions.find(q => q.id === qId));
}

function formatCompanyAnswers(answers) {
    const formattedAnswers = {};
    for(const questionId in answers) {
        const providedAnswers = answers[questionId];
        if(isEmpty(providedAnswers)) continue;
        formattedAnswers[questionId] = asArray(providedAnswers);
    }
    return formattedAnswers;
}

function parseCompanyAnswers(evaluation, category, answers) {
    const questions = getAllowedQuestions(evaluation, category);
    const parsedAnswers = {};
    if(isEmpty(answers) || isEmpty(questions)) return {};
    for(const question of questions) {
        if(isEmpty(answers[question.id])) continue;
        const value = asArray(answers[question.id]);
        if(question.multiple) parsedAnswers[question.id] = value;
        else parsedAnswers[question.id] = value[0];
    }
    return parsedAnswers;
}

function validateCompanyAnswers(evaluation, category, answers) {
    const allowedQuestions = getAllowedQuestions(evaluation, category);
    const allowedQuestionIds = allowedQuestions.map(q => q.id);
    const sentQuestionIds = Object.keys(answers);
    if(sentQuestionIds.some(qId => !allowedQuestionIds.includes(qId))) return false;
    for(const question of allowedQuestions) {
        const allowedAnswersIds = question.answers.map(a => a.id);
        const providedAnswerIds = answers[question.id];
        if(isUndefined(providedAnswerIds)) return false;
        if(!isArray(providedAnswerIds) || isEmpty(providedAnswerIds)) return false;
        if(!question.multiple && providedAnswerIds.length > 1) return false;
        if(providedAnswerIds.some(a => !isString(a) || !allowedAnswersIds.includes(a))) return false;
    }
    return true;
}

module.exports = {
    getAllQuestionIds,
    validateCompanyAnswers,
    formatCompanyAnswers,
    parseCompanyAnswers,
    hasAllowedQuestions,
    getAllowedQuestions
};